<template>
    <div>
        <div id="config">
            <ConfigHeader></ConfigHeader>

            <div class="configTuning">
                <div class="tuningHeader">
                    <p class="intro">
                        Here you will find guidance on how to tune certain postgresql.conf parameters. Not all tunable parameters are included. Use it at your own risk. There are no guarantees provided whether following this guidance will lead to a better or worse performance.</br>
                        <strong>If you want professional advice <a href="mailto:postgresqlconf@ongres.com" title="postgresqlconf@ongres.com">contact us</a>.</strong>
                    </p>
                    <div class="levelSelector floatRight">
                        <button @click="setTuningLevel(1)" class="btn basic" :class="tuningLevel == 1 ? 'active' : ''"><span></span> Basic ({{ configuredCount.basic + '/' + paramsCount.basic }})</button>
                        <button @click="setTuningLevel(2)"  class="btn intermediate" :class="tuningLevel == 2 ? 'active' : ''"><span></span> Intermediate ({{ configuredCount.intermediate + '/' + paramsCount.intermediate }})</button>
                        <button @click="setTuningLevel(3)"  class="btn advanced" :class="tuningLevel == 3 ? 'active' : ''"><span></span> Advanced ({{ configuredCount.advanced + '/' + paramsCount.advanced }})</button>
                    </div>
                </div>
                <div class="tuningHelp" ref="tuningHelp">
                    <div class="hint" ref="hint">
                        <h2>Help</h2>
                        <p v-if="editName.length">
                            <template v-for="param in tuningGuide.params" v-if="editName == param.name">
                                <h4>{{ param.name }}</h4><br>
                                {{ param.hint }}
                            </template>
                        </p>
                        <p v-else>
                            Click on any param to get help and tips on how to tune it.
                        </p>
                    </div>
                    <div class="help" v-if="editName.length && (editName == param.name)" v-for="param in defaultParams">
                        <h2>Documentation</h2>
                        
                        <span v-if="param.hasOwnProperty('showDefault')"><strong>Default: <code>{{ param.showDefault }}</code></strong></span><br><br>
                        <span v-if="param.hasOwnProperty('short')"><strong>{{ param.short }}</strong></span><br><br>
                        <span class="descText" v-if="param.hasOwnProperty('description')" v-html="param.description"></span><br><br>
                    
                        <ul>
                            <li v-if="param.hasOwnProperty('type')"><span>Type: <code>{{ param.type }}</code></span></li>
                            <li v-if="param.hasOwnProperty('min')"><span>Min: <code>{{ param.min }}</code></span></li>
                            <li v-if="param.hasOwnProperty('max')"><span>Max: <code>{{ param.max }}</code></span></li>
                            <li v-if="param.hasOwnProperty('unit')"><span>Unit: <code>{{ param.unit }}</code></span></li>
                            <li v-if="param.hasOwnProperty('context')"><span>Context: <code>{{ param.context }}</code></span></li>
                            <li v-if="param.hasOwnProperty('restart')"><span>Restart: <code>{{ param.restart }}</code></span></li>
                            <li v-if="param.hasOwnProperty('values')"><span>Values: <code>{{ param.values }}</code></span></li>
                        </ul>
                    </div>
                </div>
                <div id="params" class="tuningCards" ref="tuningCards">
                    <template v-for="conf in configs" v-if="($route.name == 'PublicTuningGuide') || (conf.configId == $route.params.id)">
                        <div id="customParams">
                            <template v-for="category in tuningGuide.categories" v-if="isReady">
                                <div class="card customParams" v-if="!( (tuningLevel == 1) && (category == 'Vacuum / Autovacuum'))">
                                    <h2>{{ category }}</h2>
                                    <table class="textLeft">
                                        <thead>
                                            <tr>
                                                <th class="name">Name</th>
                                                <th class="value">Value</th>
                                                <th class="set">Set</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr class="noResults">
                                                <td colspan="3" class="textCenter">
                                                    <p>No params matched your search term.</p>
                                                </td>
                                            </tr>
                                            <tr v-for="(param, index) in tuningGuide.params" v-if="(param.category == category) && (param.level <= tuningLevel)" tabindex="-1" :key="'param-'+index" :id="'custom-'+param.name" class="base" :class="[!isValid(param.name) ? 'error' : '', (editName == param.name) ? 'edit' : '', (searchText.length && ( !param.name.toLowerCase().includes(searchText.toLowerCase()))) ? 'hide' : '']">
                                                <td class="name" @click="editParamByName(param.name)">
                                                    <code><span>{{ param.name }}</span></code>
                                                    <div class="bgGradient"></div>
                                                </td>
                                                <td class="value">
                                                    <template v-if="isValid(param.name)">
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25"><circle opacity="0.1" fill="#4D4F5C" enable-background="new    " cx="12.5" cy="12.5" r="12.5"/><path fill="#4D4F5C" d="M8.3,11.6c-0.4,0-0.7-0.1-0.9-0.4C7.1,10.9,7,10.6,7,10.3c0-0.4,0.1-0.7,0.4-1C7.6,9,8,8.9,8.3,8.9h8.3c0.4,0,0.7,0.1,0.9,0.4c0.3,0.2,0.4,0.6,0.4,0.9c0,0.4-0.1,0.7-0.4,1c-0.2,0.3-0.6,0.4-0.9,0.4L8.3,11.6zM8.3,16.1c-0.4,0-0.7-0.1-0.9-0.4C7.1,15.5,7,15.1,7,14.8c0-0.4,0.1-0.7,0.4-1c0.2-0.3,0.6-0.4,0.9-0.4h8.3c0.4,0,0.7,0.1,0.9,0.4c0.3,0.2,0.4,0.6,0.4,0.9c0,0.4-0.1,0.7-0.4,1c-0.2,0.3-0.6,0.4-0.9,0.4L8.3,16.1z"/></svg>
                                                    </template>
                                                    <template v-else>
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25"><path fill="#FF2600" d="M12.5,0C5.6,0,0,5.6,0,12.5S5.6,25,12.5,25S25,19.4,25,12.5S19.4,0,12.5,0z M12.5,19.5c-0.8,0-1.5-0.7-1.5-1.5s0.7-1.5,1.5-1.5c0.8,0,1.5,0.7,1.5,1.5S13.3,19.5,12.5,19.5z M14,14c0,0.8-0.7,1.5-1.5,1.5c-0.8,0-1.5-0.7-1.5-1.5V7c0-0.8,0.7-1.5,1.5-1.5C13.3,5.5,14,6.2,14,7V14z"/></svg>
                                                        <span class="error tooltipText hideMe">Error: {{ getParamError(param.name) }}</span>
                                                    </template>

                                                    <div class="editModeOn">
                                                        <template v-if="editName != param.name">
                                                            <template v-if="!isSet(param.name)">
                                                                <template v-for="defaultValue in defaultParams" v-if="param.name == defaultValue.name">                                                                
                                                                    <textarea class="fullValue isDefaultValue" rows="1" @keydown.prevent @focus="editParamByName(param.name)" :value="defaultValue.showDefault" spellcheck="false"></textarea>
                                                                    <span v-if="defaultValue.hasOwnProperty('default')" class="tooltipText hideMe"> {{ defaultValue.showDefault }} </span>
                                                                </template>
                                                            </template>
                                                            <template v-else>
                                                                <template v-for="p in params" v-if="p.name == param.name">
                                                                    <span v-if="(p.setting.value == getDefaultValue(param.name))" class="isDefaultValue" data-tooltip="This parameter is using its default value."></span>
                                                                    <textarea class="fullValue" rows="1" @keydown.prevent  @focus="editParamByName(param.name)" :value="(p.setting.hasOwnProperty('unit') ? ''+p.setting.value+' '+p.setting.unit : p.setting.value)" spellcheck="false"></textarea>
                                                                    <span class="tooltipText hideMe"> {{ p.setting.value }} </span>
                                                                </template>
                                                            </template>
                                                        </template>
                                                        <template v-else>
                                                            <template v-for="defaultValue in defaultParams" v-if="param.name == defaultValue.name">
                                                                <template v-if="defaultValue.hasOwnProperty('octalFormat')">
                                                                    <input v-model="currentParam.setting.value" class="customValue" @keypress="removeLetters(true)" :disabled="validateConfig" :title="validateConfig ? 'Login or signup to edit' : ''" />
                                                                </template>
                                                                <template v-else-if="(defaultValue.type == 'string')">
                                                                    <textarea rows="1" v-model="currentParam.setting.value" class="customValue" :disabled="validateConfig" :title="validateConfig ? 'Login or signup to edit' : ''" spellcheck="false"></textarea>
                                                                </template>
                                                                <template v-else-if="defaultValue.type == 'bool'">
                                                                    <label class="on" :for="'unitOn-'+param.name">
                                                                        <input type="radio" value="on" :name="'unitBool-'+param.name" :id="'unitOn-'+param.name" v-model="currentParam.setting.value" class="customValue" :disabled="validateConfig" :title="validateConfig ? 'Login or signup to edit' : ''">
                                                                        <span class="radioLabel">ON</span>
                                                                    </label>
                                                                    <label class="off" :for="'unitOff-'+param.name">
                                                                        <input type="radio" value="off" :name="'unitBool-'+param.name" :id="'unitOff-'+param.name" v-model="currentParam.setting.value" class="customValue" :disabled="validateConfig" :title="validateConfig ? 'Login or signup to edit' : ''">
                                                                        <span class="radioLabel">OFF</span>
                                                                    </label>
                                                                </template>
                                                                <template v-else-if="(defaultValue.type == 'real') || (defaultValue.type == 'integer')">
                                                                    <input type="number" step="any" @keypress="removeLetters(false)" v-model="currentParam.setting.value" class="customValue" :disabled="validateConfig" :title="validateConfig ? 'Login or signup to edit' : ''">
                                                                </template>
                                                                <template v-else-if="defaultValue.type == 'enum'">
                                                                    <select v-model="currentParam.setting.value" class="customValue" :disabled="validateConfig" :title="validateConfig ? 'Login or signup to edit' : ''">
                                                                        <option v-for="value in defaultValue.values">{{ value }}</option>
                                                                    </select>
                                                                </template>

                                                                <template v-if="defaultValue.hasOwnProperty('subtype')">
                                                                    <select v-model="currentParam.setting.unit" class="customValue unit" :disabled="validateConfig" :title="validateConfig ? 'Login or signup to edit' : ''">
                                                                        <option v-for="unit in units[defaultValue.subtype]" :value="unit">{{ unit }}{{ (defaultValue.hasOwnProperty('unit') && (defaultValue.unit == unit)) ? ' (default)' : '' }}</option>
                                                                        <option v-if="defaultValue.hasOwnProperty('min') && (defaultValue.min == '-1')" value="" :selected="currentParam.setting.value == '-1'">{{ defaultValue.unit }} (default)</option>
                                                                    </select>
                                                                </template>
                                                            </template>
                                                        </template>
                                                    </div>
                                                    <div class="bgGradient"></div>
                                                </td>
                                                <td class="comment set">
                                                    <template v-if="editName == param.name">
                                                        <div class="action">
                                                            <button type="button" tabindex="0" @click="setParamByName(param.name, false, true)" class="saveParam" title="Save Changes">
                                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25"><path fill="#00ADB5" d="M12.5,25c-3.3,0-6.5-1.3-8.8-3.7C1.3,19,0,15.8,0,12.5C0,9.2,1.3,6,3.7,3.7C6,1.3,9.2,0,12.5,0C19.4,0,25,5.6,25,12.5c0,3.3-1.3,6.5-3.7,8.8C19,23.7,15.8,25,12.5,25z M7.9,11.8c-0.3,0-0.5,0.1-0.7,0.3c-0.2,0.2-0.3,0.4-0.3,0.7c0,0.3,0.1,0.5,0.3,0.7l3.3,3.3c0.2,0.2,0.4,0.3,0.7,0.3c0.3,0,0.5-0.1,0.7-0.3l6.4-6.4c0.4-0.4,0.4-1,0-1.4c0,0,0,0,0,0c-0.2-0.2-0.4-0.3-0.7-0.3c-0.3,0-0.5,0.1-0.7,0.3l-5.7,5.7l-2.5-2.5C8.4,11.9,8.2,11.8,7.9,11.8L7.9,11.8z"/></svg>
                                                            </button>
                                                            <button type="button" tabindex="0" @click="cancelEdit()" class="discardParam" title="Discard Changes">
                                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25"><path fill="#00ADB5" d="M12.5,25c-3.3,0-6.5-1.3-8.8-3.7C1.3,19,0,15.8,0,12.5C0,9.2,1.3,6,3.7,3.7C6,1.3,9.2,0,12.5,0c3.3,0,6.5,1.3,8.8,3.7C23.7,6,25,9.2,25,12.5c0,3.3-1.3,6.5-3.7,8.8C19,23.7,15.8,25,12.5,25z M8.6,7.6c-0.3,0-0.5,0.1-0.7,0.3c-0.4,0.4-0.4,1,0,1.4l3.2,3.2l-3.2,3.2c-0.4,0.4-0.4,1,0,1.4c0,0,0,0,0,0c0.2,0.2,0.4,0.3,0.7,0.3c0.3,0,0.5-0.1,0.7-0.3l3.2-3.2l3.2,3.2c0.2,0.2,0.4,0.3,0.7,0.3c0.3,0,0.5-0.1,0.7-0.3c0.2-0.2,0.3-0.4,0.3-0.7c0-0.3-0.1-0.5-0.3-0.7l-3.2-3.2l3.2-3.2c0.2-0.2,0.3-0.4,0.3-0.7s-0.1-0.5-0.3-0.7c-0.2-0.2-0.4-0.3-0.7-0.3c-0.3,0-0.5,0.1-0.7,0.3l-3.2,3.2L9.3,7.9C9.1,7.7,8.9,7.6,8.6,7.6z"/></svg>
                                                            </button>
                                                        </div>
                                                    </template>
                                                    <template v-else>
                                                        <div class="action" :class="isSet(param.name) ? 'set' : ''">                                                            
                                                            <button type="button" tabindex="0" @click="addParam(param.name)" class="saveParam" title="Add Parameter">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" class="noSet"><g transform="translate(-15.274)"><rect width="25" height="25" rx="12.5" transform="translate(15.274)" fill="#4d4f5c" opacity="0.25"/><path d="M105.16,91.812a1.384,1.384,0,0,0-1.988,0l-6.96,7.119L94.224,96.9a1.385,1.385,0,0,0-1.99,0h0a1.463,1.463,0,0,0,0,2.034l2.984,3.05a1.384,1.384,0,0,0,1.988,0l7.954-8.135A1.46,1.46,0,0,0,105.16,91.812Z" transform="translate(-70.93 -84.396)" fill="#4d4f5c"/></g></svg>
                                                            </button>
                                                            <button @click="setDefaultByName(param.name)" class="restoreDefault" title="Restore to Default Value">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" class="isSet"><g transform="translate(-15.274)"><rect width="25" height="25" rx="12.5" transform="translate(15.274)" fill="#00adb5" opacity="0.25"/><path d="M105.16,91.812a1.384,1.384,0,0,0-1.988,0l-6.96,7.119L94.224,96.9a1.385,1.385,0,0,0-1.99,0h0a1.463,1.463,0,0,0,0,2.034l2.984,3.05a1.384,1.384,0,0,0,1.988,0l7.954-8.135A1.46,1.46,0,0,0,105.16,91.812Z" transform="translate(-70.93 -84.396)" fill="#00adb5"/></g></svg>
                                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25" class="restore"><path fill="#00ADB5" d="M12.5,0C5.6,0,0,5.6,0,12.5S5.6,25,12.5,25S25,19.4,25,12.5S19.4,0,12.5,0z M13,18.5h-2.2c0,0-0.1,0-0.1,0c-0.6,0-1.1-0.6-1.1-1.2c0-0.6,0.5-1.1,1.1-1.1h2.4c1.7,0.1,3.2-1.2,3.3-2.9c0-0.1,0-0.2,0-0.3c0-1.7-1.4-3-3.1-3H9.6v1.1c0,0.1,0,0.1-0.1,0.2c-0.1,0.1-0.3,0.1-0.4,0L6.3,8.9V8.8l2.8-2.2c0.1-0.1,0.1-0.1,0.2-0.1c0.2,0,0.3,0.1,0.3,0.3v1.1h3.7c2.9,0,5.2,2.2,5.4,5.1C18.6,16,16.1,18.5,13,18.5z"/></svg>
                                                            </button>
                                                        </div>
                                                    </template>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </div>
                    </template>
                </div>
                <div id="tooltip">
                    <div class="info"></div>
                </div>
            </div>

        </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mixin } from '../components/mixins/mixin'
import { configParams } from '../components/mixins/configParams'

// Components import
import ConfigHeader from '../components/ConfigHeader'

export default {
    name: 'TuningGuide',

    mixins: [mixin, configParams],

    data: function() {
		return {
            help: '',
            tuningLevel: 1,
            noBasic: 'Vacuum / Autovacuum',
            currentParam: {
                name: '',
                setting: {
                    value: '',
                    unit: ''
                }
            },
            tuningGuide: {
                categories: ["Processes settings","Memory settings","Logging","Checkpoint and bgwriter", "Vacuum / Autovacuum","Replicas","Planner","Miscelanea"],
                params: [
                    {
                        name: "max_connections",
                        category: "Processes settings",
                        level: 1,
                        hint: "For most use cases, you should use connection pooling. When using connection pooling in transaction mode, set this parameter as a low multiple (2-5x) of the number of cores on the server.\n\nIf running on session mode, to the minimum number of concurrent users that cannot be queued.\n\nIf not using connection pooling, to the required number of concurrent users. Performance may be suboptimal."
                    },
                    {
                        name: "max_worker_processes",
                        category: "Processes settings",
                        level: 2,
                        hint: "An easy approach is to set it to 75% up to 90% (for high core count) of the number of cores. A more detailed approach is the sum of all worker parameters."
                    },
                    {
                        name: "max_parallel_workers_per_gather",
                        category: "Processes settings",
                        level: 3,
                        hint: "Beware of setting to a high value for high throughput OLTP systems. Oftentimes it is better to leave it on 1 or 2 at most. Raise it for analytical/DW systems, always to a fraction of cores.\n\nThis parameter is affected by work_mem, we recommend to review that value accordingly."
                    },
                    {
                        name: "max_parallel_workers",
                        category: "Processes settings",
                        level: 3,
                        hint: "Adjust it accordingly to max_parallel_workers_per_gather (which is limited by this parameter)."
                    },
                    {
                        name: "max_wal_senders",
                        category: "Processes settings",
                        level: 2,
                        hint: "Set it to the maximum number of replicas you expect to have in the cluster."
                    },
                    {
                        name: "shared_buffers",
                        category: "Memory settings",
                        level: 1,
                        hint: "Conventional wisdom suggests 25% of the RAM. Do your own benchmarks varying this parameter. If you are into hundreds of GBs, consider setting up huge pages.\n\nAdjust accordingly for non-dedicated servers, considering that each Postgres instance will reserve its own memory allocations. This variable is in direct relation with OS kernel parameters `shmmax` and `shmall`."
                    },
                    {
                        name: "huge_pages",
                        category: "Memory settings",
                        level: 1,
                        hint: "Set to ‘off’.\n\nIf you really know what you are doing, set to ‘on’ and configure sysctl appropriately.\n\nAvoid ‘try’."
                    },
                    {
                        name: "work_mem",
                        category: "Memory settings",
                        level: 2,
                        hint: "Setting this parameter requires inspecting the memory usage in your queries. If this value falls short in a query execution, the engine will use memory and disk, impacting on its performance. Although, keeping this value too large, could destabilize the node throughput when dealing with too many concurrent operations.\n\nFor OLTP queries that do not require complex joins or sorting lots of data, this value may be in the low MBs. For OLAP-style queries, and few concurrent queries, it may grow into the GB range.\n\nMultiply by max_connections * N (being N a small single-digit number) to estimate the maximum amount of total memory potential consumed by Postgres process. Adjust based on it. If possible, raise the default value."
                    },
                    {
                        name: "maintenance_work_mem",
                        category: "Memory settings",
                        level: 2,
                        hint: "Increasing this value will speed up maintenance tasks such as VACUUM and index rebuilds, therefore it is recommended to raise it notably with large RAM resources and write operations. Note that this is -unless directly controlled by autovacuum_work_mem- also the amount of memory *each* autovacuum workers might use."
                    },
                    {
                        name: "logging_collector",
                        category: "Logging",
                        level: 1,
                        hint: "Unless you don’t care about logs, set it to ‘on’."
                    },
                    {
                        name: "log_destination",
                        category: "Logging",
                        level: 1,
                        hint: "If primary log consumption is human, use ‘stderr’. If logs need to be sent to collector services or processed by tools, you probably need ‘csvlog’.\n\nIf in doubt, use ‘csvlog’."
                    },
                    {
                        name: "log_rotation_size",
                        category: "Logging",
                        level: 1,
                        hint: "10MB is probably very low. Ideally, avoid GB-sized log files."
                    },
                    {
                        name: "log_checkpoints",
                        category: "Logging",
                        level: 1,
                        hint: "Turn it on, you want to know when checkpoints happen."
                    },
                    {
                        name: "log_connections",
                        category: "Logging",
                        level: 2,
                        hint: "Logging connections is useful to understand line-rate information about session establishment. Turn it on unless you don’t use connection pooling and you are dealing with a huge number of connections (if this is the case: setup connection pooling)."
                    },
                    {
                        name: "log_disconnections",
                        category: "Logging",
                        level: 2,
                        hint: "If you turn on ‘log_connections’, you may also want to turn this on to understand session durations."
                    },
                    {
                        name: "log_line_prefix",
                        category: "Logging",
                        level: 3,
                        hint: "Enrich your log information! If you want to use external log processing tools like PgBadger, check the documentation to make sure you use a compatible ‘log_line_prefix’."
                    },
                    {
                        name: "log_temp_files",
                        category: "Logging",
                        level: 2,
                        hint: "Very useful to detect excessive usage of temporary files (which may be indicative of low work_mem or maintenance_work_mem)."
                    },
                    {
                        name: "log_min_duration_statement",
                        category: "Logging",
                        level: 1,
                        hint: "Don’t set it to ‘0’ (log all queries) unless you have and will always have a low-traffic service. ‘0’ may bring down a very busy server.\n\nFind a reasonable value for your application (e.g. ‘1s’) and use other tools to understand query performance, like pg_stat_statements or pg_stat_monitor."
                    },
                    {
                        name: "checkpoint_timeout",
                        category: "Checkpoint and bgwriter",
                        level: 2,
                        hint: "Normally you want to raise it (e.g. ‘15min’). The larger the parameter is, you may experience larger I/O spikes (which may be smoothed out tuning checkpoint_completion_target and bgwriter activity) and the larger recovery time you may experience after a database crash. However, more frequent checkpoints create in aggregation more I/O, so raising it typically improves I/O usage."
                    },
                    {
                        name: "checkpoint_completion_target",
                        category: "Checkpoint and bgwriter",
                        level: 3,
                        hint: "Raise it to smooth I/O activity of checkpoints over larger periods of time. If you rely on this value for feeding non-streaming replicas, it is recommended to keep this within a low percentage. On development instances, it is usual to set it to ‘0.9’."
                    },
                    {
                        name: "min_wal_size",
                        category: "Checkpoint and bgwriter",
                        level: 1,
                        hint: "The default value is very low. Unless you have significant disk space restrictions, raise it up to 1GB."
                    },
                    {
                        name: "max_wal_size",
                        category: "Checkpoint and bgwriter",
                        level: 2,
                        hint: "Unless there are disk space constraints, raise this value to make sure automatic checkpoints are typically caused by timeout and not by disk space.\n\nIncreasing this value increases the recovery time after a database crash."
                    },
                    {
                        name: "checkpoint_warning",
                        category: "Checkpoint and bgwriter",
                        level: 1,
                        hint: "This alerts you in the logs if checkpoints are happening too often. Set it to a fraction of checkpoint_timeout (e.g. ‘5min’) or ‘1min’ if checkpoint_timeout is not tuned."
                    },
                    {
                        name: "bgwriter_delay",
                        category: "Checkpoint and bgwriter",
                        level: 3,
                        hint: "On busy systems, reduce it to increase background writer activity. Values in multiples of 10ms."
                    },
                    {
                        name: "bgwriter_lru_maxpages",
                        category: "Checkpoint and bgwriter",
                        level: 3,
                        hint: "Increase this value to further increase the activity of the background writer on busy databases."
                    },
                    {
                        name: "vacuum_cost_limit",
                        category:  "Vacuum / Autovacuum",
                        level: 2,
                        hint: "Normally you want to raise it (e.g. ‘15min’). The larger the parameter is, you may experience larger I/O spikes (which may be smoothed out tuning checkpoint_completion_target and bgwriter activity) and the larger recovery time you may experience after a database crash. However, more frequent checkpoints create in aggregation more I/O, so raising it typically improves I/O usage."
                    },
                    {
                        name: "autovacuum_max_workers",
                        category:  "Vacuum / Autovacuum",
                        level: 2,
                        hint: "Raise it to smooth I/O activity of checkpoints over larger periods of time. If you rely on this value for feeding non-streaming replicas, it is recommended to keep this within a low percentage. On development instances, it is usual to set it to ‘0.9’."
                    },
                    {
                        name: "autovacuum_vacuum_scale_factor",
                        category:  "Vacuum / Autovacuum",
                        level: 2,
                        hint: "The default value is very low. Unless you have significant disk space restrictions, raise it up to 1GB."
                    },
                    {
                        name: "autovacuum_analyze_scale_factor",
                        category:  "Vacuum / Autovacuum",
                        level: 2,
                        hint: "Unless there are disk space constraints, raise this value to make sure automatic checkpoints are typically caused by timeout and not by disk space.\n\nIncreasing this value increases the recovery time after a database crash."
                    },
                    {
                        name: "autovacuum_freeze_max_age",
                        category:  "Vacuum / Autovacuum",
                        level: 3,
                        hint: "This alerts you in the logs if checkpoints are happening too often. Set it to a fraction of checkpoint_timeout (e.g. ‘5min’) or ‘1min’ if checkpoint_timeout is not tuned."
                    },
                    {
                        name: "vacuum_cost_delay",
                        category:  "Vacuum / Autovacuum",
                        level: 3,
                        hint: "On busy systems, reduce it to increase background writer activity. Values in multiples of 10ms."
                    },
                    {
                        name: "max_replication_slots",
                        category: "Replicas",
                        level: 1,
                        hint: "Set it to a number higher than the maximum number of expected replicas in the cluster plus maximum number of logical replication consumers plus a small additional margin."
                    },
                    {
                        name: "hot_standby_feedback",
                        category: "Replicas",
                        level: 3,
                        hint: "Set to ‘on’ if you want to avoid read-only query cancellations running in the replicas. However, this creates pressure on the master (increased bloat, WAL disk space consumption)."
                    },
                    {
                        name: "max_standby_streaming_delay",
                        category: "Replicas",
                        level: 3,
                        hint: "Set to ‘0’ if your primary use of the replica is for failover and read-only queries run may get cancelled.\n\nSet it to the maximum lag tolerable, which also acts as a ceiling for the maximum query duration before a conflicting query gets cancelled, if ‘hot_standby_feedback’ is set."
                    },
                    {
                        name: "effective_cache_size",
                        category: "Planner",
                        level: 2,
                        hint: "Set it to (approximately): system RAM - (shared_buffers + work_mem * max_connections * 2) * 1.1, or a lower value if the server is not dedicated exclusively to PostgreSQL.\n\nNote that this setting does not affect the amount of physical RAM used by queries."
                    },
                    {
                        name: "default_statistics_target",
                        category: "Planner",
                        level: 3,
                        hint: "Raise this value if you have large tables. Understand well the effects on raising this value. Medium-sized systems typically do well with values around 200 and large systems may need to increase it to 500 or 1000."
                    },
                    {
                        name: "random_page_cost",
                        category: "Planner",
                        level: 1,
                        hint: "Set it to 1.x (e.g. ‘1.2’) if your disk technology has a random access profile similar to that of SSDs."
                    },
                    {
                        name: "jit",
                        category: "Planner",
                        level: 2,
                        hint: "You may want to disable it for OLTP systems with simple queries."
                    },
                    {
                        name: "cluster_name",
                        category: "Miscelanea",
                        level: 1,
                        hint: "Set it if you have more than one PostgreSQL instance running per server, to distinguish them when listing processes and logs.\n\nAlso convenient to set it anyway."
                    },
                    {
                        name: "archive_mode",
                        category: "Miscelanea",
                        level: 1,
                        hint: "As a common and recommended practice, in order to avoid unnecessary database restarts, set it to ‘on’ and configure the archive_command accordingly (see archive_command).\n\nHowever, archiving is recommended in any environment that requires backups and data consistency.\n\nYou may leave it disabled for databases which are not the source of truth or which are used just for testing purposes, to minimize the storage used."
                    },
                    {
                        name: "archive_command",
                        category: "Miscelanea",
                        level: 1,
                        hint: "If you are not planning to do archiving (hint: you should) set it to ‘/bin/true’ or `exit 0`. This will allow future changes without restart.\n\nOtherwise, set it to the appropriate command for the archiving software you are using. If a script is used, deal appropriately with error conditions in the script."
                    },
                    {
                        name: "shared_preload_libraries",
                        category: "Miscelanea",
                        level: 2,
                        hint: "Set it to the requirements of the extensions you are planning to use. At the very least, you probably want ‘pg_stat_statements’ or ‘pg_stat_monitor’."
                    },
                    {
                        name: "track_activity_query_size",
                        category: "Miscelanea",
                        level: 3,
                        hint: "If you use track query performance, you may want to raise this a bit (recommended not above 8KB) to log up to 8KB of your queries text."
                    },
                ]
            }
        }
    },

    components: {
        ConfigHeader
    },

    computed: {

        configuredCount() {
            const vc = this
            var count = {
                "1": 0,
                "2": 0,
                "3": 0
            }

            vc.tuningGuide.params.forEach(function(item) {
                if(vc.isSet(item.name))
                    count[item.level.toString()]++
            })

            return {
                "basic": count[1],
                "intermediate": count[1] + count[2],
                "advanced": count[1] + count[2] + count[3],
            }
        },

        paramsCount() {
            const vc = this
            var count = {
                "1": 0,
                "2": 0,
                "3": 0
            }

            vc.tuningGuide.params.forEach(function(item) {
                count[item.level.toString()]++
            })

            return {
                "basic": count[1],
                "intermediate": count[1] + count[2],
                "advanced": count[1] + count[2] + count[3],
            }
        },
    },

    methods: {

        setParamByName( param ) {
            const vc = this;

            if( vc.$route.name == 'PublicTuningGuide') { 
                axios
                .post(process.env.VUE_APP_API_URL+'/validate/param/'+vc.pgVersion, vc.currentParam)
                .then( function(response){
                   if(typeof response !== 'undefined') {
                        if(vc.isSet(param)) {
                            vc.params.forEach(function(p){
                                if(p.name == param) {
                                    p.setting.value = vc.currentParam.setting.value
                                    p.valid = true
                                    
                                    if(p.setting.hasOwnProperty('unit'))
                                        p.setting.unit = vc.currentParam.setting.unit
                                    
                                    return false
                                }
                            })
                        } else {
                            vc.params.push({
                                name: vc.currentParam.name,
                                setting: {
                                    value: vc.currentParam.setting.value,
                                    ...(vc.currentParam.setting.hasOwnProperty('unit') && ( {unit: vc.currentParam.setting.unit }) ),
                                },
                                valid: true
                            })
                        }
                        vc.storeTmpConfig()
                    }

                    vc.editIndex = -1
                    vc.editName = ''

                }).catch(function(err) {

                    if(typeof err.response !== "undefined") {
                        vc.notify('<p>There are some errors on the value specified for <strong><code>' + param + '</code></strong>. Mouse hover the error icon to see more details.</p>', false, 'error')
                                                
                        if(vc.isSet(param)) {
                            vc.params.forEach(function(p){
                                if(p.name == param) {
                                    p['valid'] = false
                                    p.setting.value = vc.currentParam.setting.value
                                    
                                    if(vc.currentParam.setting.hasOwnProperty('unit'))
                                        p.setting.unit = vc.currentParam.setting.unit
                                        
                                    return false
                                }
                            })
                        } else {
                            vc.params.push({
                                name: vc.currentParam.name,
                                setting: {
                                    value: vc.currentParam.setting.value,
                                    ...(vc.currentParam.setting.hasOwnProperty('unit') && ( {unit: vc.currentParam.setting.unit }) ),
                                },
                                valid: false,
                                error: err.response.data.error
                            })
                        }
                        vc.storeTmpConfig()
                    }

                    vc.editIndex = -1
                    vc.editName = ''
                    
                    console.log(err);
                });

            } else if(vc.isSet(param) && (vc.editIndex !== -1)) {
                vc.setParam(vc.editIndex)
            } else {
                const vc = this;
            
                axios
                .patch(process.env.VUE_APP_API_URL+'/configs/'+vc.$route.params.id+'/postgresql.conf', {params: [vc.currentParam]})
                .then( function(response){
                    if(response.data.hasOwnProperty('error'))
                        if(response.data.error.length && (response.data.inserted > 0))
                            vc.notify('<p>There are some errors on the value specified for <strong><code>' + response.data.error.toString() + '</code></strong>. Mouse hover the error icon to see more details.</p>', false, 'error')
                        else if (response.data.error.length && (response.data.inserted == 0))
                            vc.notify('<p>There were some errors when trying to add <strong><code>' + response.data.error.toString() + '</code></strong> to your configuration. Please check your input and try again.</p>', false, 'error')

                    vc.fetchData()
                }).catch(function(err) {
                    console.log(err);
                });

                vc.editIndex = -1
                vc.editName = ''
            }
        },

        setTuningLevel( level ) {
            this.tuningLevel = level
            const tuningCookie = this.getCookie('tuningGuide')
            const cookiePos = tuningCookie.indexOf(this.configId)
            if(cookiePos !== -1) {
                document.cookie = 'tuningGuide=' + tuningCookie.substring(0,cookiePos + this.configId.length + 1) + level + tuningCookie.substring(cookiePos + this.configId.length + 3) + '; Path=/; SameSite=Lax;  expires=Fri, 31 Dec 9999 23:59:59 GM';
            } else {
                document.cookie = 'tuningGuide=' + ((tuningCookie.length) ? tuningCookie + '&' : '') + this.configId + ':' + level + '; Path=/; SameSite=Lax;  expires=Fri, 31 Dec 9999 23:59:59 GM';
            }
        },

        getTuningLevel() {
            const vc = this
            const tuningCookie = vc.getCookie('tuningGuide')
            const cookiePos = tuningCookie.indexOf(vc.$route.params.id)
            
            return (cookiePos != -1) ? tuningCookie.substring(cookiePos + vc.$route.params.id.length + 1, cookiePos + vc.$route.params.id.length + 2) : 1
        },

        getParamError( param ) {
            const p = this.params.find(p => ( param == p.name ));
            return p.error
        },

        validateAllParams() {
            const vc = this;

            if(vc.params.length) {
                axios
                .post(process.env.VUE_APP_API_URL+'/validate/parameters', {
                    version: vc.pgVersion,
                    params: vc.cleanupParams()
                })
                .then( function(response){
                    
                    if(typeof response !== 'undefined')
                        vc.storeTmpConfig()
                    
                    vc.editIndex = -1
                    vc.editName = ''

                }).catch(function(err) {

                    if(typeof err.response !== "undefined") {
                        vc.notify('<p>Some parameters may have errors according to the selected Postgres version. Mouse hover the error icon to see more details.</p>', false, 'error')
                        
                        vc.params = [...err.response.data.params]
                        vc.storeTmpConfig()
                    }

                    vc.editIndex = -1
                    vc.editName = ''
                    
                    console.log(err);
                });
            }
        }

    }

}
</script>



<style scoped>
    #main {
        padding: 0;
        overflow: hidden;
    }


    .blueClass {
        color: blue;
    }

    li.noParams {
    display: none;
    }

    li.noParams:first-child:last-child {
        display: block;
    }

    .open.settings #main {
        width: calc(100vw - 400px);
    }


    .configTuning #customParams {
        box-shadow: none !important;
        padding: 0;
    }

    .configTuning .action {
        opacity: 1 !important;
        display: inline !important;
    }

    .configTuning .action.set .saveParam, .configTuning .action:not(.set) .restoreDefault, .configTuning .action.set .restore, .configTuning .action.set .restoreDefault:hover .isSet {
        display: none;
    }

    .configTuning .action.set .restoreDefault:hover .restore {
        display: block;
    }

    .set .discardParam {
        margin-right: 0;
    }

    .set .discardParam:after {
        display: none;
    }

    span.isDefaultValue {
        position: absolute;
        width: 22px;
        height: 22px;
        border: 1px solid #A5A5A5;
        border-radius: 100%;
        left: -31px;
        top: 2px;
    }

</style>